<script>
    // 허브 격자에 띄울 채팅 고르기. 봇 빌더의 '채팅' 탭 내용물이다.
    //
    // ⭐ 이 탭의 본업은 **격자**다 — 어떤 챗을 어떤 순서로 담고, 카드가 어떤 모양인가.
    //    로고·캐릭터·소개 문구·단계별 멘트는 [기본] 탭(HubBasic), 로그인 잠금은 [설정] 탭으로 갔다.
    //    한때 전부 여기 있었는데 '채팅 고르기'와 섞여 무슨 화면인지 읽히지 않았다.
    //
    // ⚠️ 저장은 **전체 교체**다(setHubChats). 순서·라벨·표시방식이 한 화면에서 함께 바뀌므로
    //    부분 갱신보다 통째 교체가 단순하고, 화면과 DB가 어긋날 여지가 없다.
    //    그래서 '저장'을 누르기 전까지는 서버에 아무것도 안 간다.
    import { createEventDispatcher, onDestroy, onMount, tick } from "svelte";
    import { PRConnect } from "@projectroom/ui-lib";
    import { Section, Button, Badge, Input, Select } from "../../lib/ui";
    import { markSaving, markSaved } from "../store/builder-store.js";
    import HubPickedCard from "./HubPickedCard.svelte";
    import HubTestSlot from "./HubTestSlot.svelte";
    import ImagePicker from "../chat-builder/ImagePicker.svelte";
    import { botAssetFolder } from "../chat-builder/imageAssetApi";
    export let botId;
    // 카드 아이콘을 이미지 자산에서 고를 때 '이 허브 폴더'를 먼저 열려고 받는다(botAssetFolder). 없으면 전체에서 고른다.
    export let bot = null;

    const dispatch = createEventDispatcher();

    let picked = [];
    let candidates = [];
    // 허브 '한 장' 설정 — 격자 한 줄에 카드를 몇 장 놓을지. 0 = 화면 폭에 맞춰 자동(기존 동작).
    // ⚠️ 카드 수·제목 길이를 아는 건 저작자뿐이라 값으로 뺐다. 고른 값은 폰에서도 그대로 지켜진다.
    let gridColumns = 0;
    // 허브 '한 장' 설정 — 카드 한 장에 무엇까지 보여줄지(full | title | thumb).
    // ⚠️ 카드별이 아니라 격자 전체다. 카드마다 줄 수가 다르면 높이가 들쭉날쭉해져 격자가 흐트러진다
    //    (카드별 축은 아래 '표시' 드롭다운 = 진행 표시가 따로 갖고 있다).
    let cardStyle = "full";
    // 허브 '한 장' 설정 — 카드 진행 표시 기본값. 카드는 다르게 하고 싶을 때만 자기 값을 갖는다(kind "" = 기본값 따름).
    // 예전엔 카드마다 골라야 했다 — 15장이면 15번 같은 값을 고르고, 새 카드를 담을 때마다 또 골랐다.
    let defaultDisplay = { kind: "progress", key: "" };
    let loading = true;
    let error = "";
    // 본 콘텐츠 스크롤 칸 — 새로 담은 카드가 보이게 끝으로 내릴 때 쓴다
    let mainScroll;
    // 후보 검색어. 계정에 챗이 쌓이면 목록이 화면을 넘겨서 찾기가 안 된다.
    // ⚠️ 서버에 다시 묻지 않는다 — 후보는 이미 한 번에 다 받아 온 목록이다(재조회는 낭비).
    let keyword = "";
    $: shown = keyword.trim()
        ? candidates.filter((c) => (c.title || "").toLowerCase().includes(keyword.trim().toLowerCase()))
        : candidates;

    // 빌더는 자동저장이 관례다(헤더의 '저장 중…/저장됨' 표시가 그것) → 저장 버튼을 두지 않는다.
    // ⚠️ 데이터 변화를 반응형으로 감시해 저장하면 최초 로드에서도 한 번 저장된다.
    //    그래서 '사용자 조작'에서만 예약한다.
    let saveTimer;
    const SAVE_DEBOUNCE_MS = 1000;   // 라벨을 타이핑하는 중에는 저장하지 않을 만큼

    // 카드에 무엇을 보여줄지. 서버는 문자열 하나로 받는다(progress | badge | none | metric:<key>).
    // 한 줄 카드 수. 4를 넘기지 않는 건 카드 폭(썸네일 32px + 제목)이 그 아래로 가면
    // 폰에서 제목이 읽히지 않아서다. 서버도 같은 범위로 자른다(HubSetup.setHubChats).
    const COLUMNS = [
        { value: 0, label: "자동 (화면 폭에 맞춰)" },
        { value: 1, label: "1개" },
        { value: 2, label: "2개" },
        { value: 3, label: "3개" },
        { value: 4, label: "4개" },
    ];

    // 카드 한 장에 무엇까지. 썸네일은 어느 쪽이든 나온다 — 카드가 통째로 비어 보이지 않으려면
    // 최소한 이미지 한 장은 있어야 하고, 이미지가 없는 챗은 제목 첫 글자가 그 자리를 채운다.
    const CARD_STYLES = [
        { value: "full", label: "썸네일 + 제목 + 설명" },
        { value: "title", label: "썸네일 + 제목" },
        { value: "thumb", label: "썸네일만" },
    ];

    // 진행 막대 = 시안의 하단 진행 줄('8/10 … 80%' + 막대).
    // count = 같은 하단 막대 + 카드 **우측 상단**에 숫자(2/3). 하단 막대는 둘 다 똑같이 뜬다 —
    //   예전엔 count 카드에 막대가 없어 같은 격자에서 숫자만 뜨는 카드와 막대가 뜨는 카드가 섞였다(PROFILE HubChatCard).
    const DISPLAY = [
        { value: "progress", label: "진행 막대 (8/10 · 80%)" },
        { value: "count", label: "진행 막대 + 우측 상단 숫자 2/3" },
        { value: "badge", label: "완료 배지" },
        { value: "metric", label: "지표(호감도 등)" },
        { value: "none", label: "표시 안 함" },
    ];
    // 카드 값이 비어 있으면(null) kind "" = 허브 기본값 따름. 기본값 자체는 fallback 으로 'progress' 를 준다.
    const splitDisplay = (v, fallback = "") => {
        const s = String(v || fallback);
        return s.startsWith("metric:") ? { kind: "metric", key: s.slice(7) } : { kind: s, key: "" };
    };
    // kind "" → null(기본값 따름). 지표인데 키가 비면 무엇을 보여줄지 몰라 서버가 받지 않는다(기본값으로 처리).
    const joinDisplay = (r) => (!r.kind ? null : r.kind === "metric" ? `metric:${r.key || ""}` : r.kind);
    const displayLabel = (r) =>
        r.kind === "metric" ? `지표${r.key ? ` (${r.key})` : ""}` : (DISPLAY.find((d) => d.value === r.kind)?.label || r.kind);

    // 챗 프로필 이미지. 이미지 자산(버전관리)이 있으면 그 URL이 우선이다 — 콘솔 목록과 같은 규칙.
    const thumbOf = (c) =>
        c.profileAssetUrl ||
        (c.profileImageContent
            ? `https://groovworks-avatalk-public-file-storage.s3.ap-northeast-2.amazonaws.com/chats/${c.chatId || c.id}/common/profile-images/${c.profileImageContent}`
            : null);

    const toCandidate = (c) => ({ ...c, thumb: thumbOf(c), published: Number(c.published) > 0 });

    // 사전 테스트·사후 점검 챗은 **본 콘텐츠와 다른 칸**으로 갈라 맨 위에 둔다. 격자에 챗이 많으면
    // 섞여 있는 두세 장을 찾기 어렵고, 그 둘은 학습자 화면에서도 격자 카드와 다르게 다뤄진다
    // (사전=격자 대신 [시작하기] 버튼 / 사후=사후 단계에서만 뜸, PROFILE HubLayout).
    // ⚠️ 칸은 화면만의 구분이 아니라 **저장 순서**도 그렇게 맞춘다(picked = 사전 → 사후 → 본 콘텐츠).
    //    두 칸을 오가며 순서를 바꿀 수 없게 해 두면 그 순서가 곧 저장 순서라 화면과 DB 가 어긋나지 않는다.
    // ⚠️ 용도(role)는 챗이 갖는다(CHATS.PURPOSE) — 챗 빌더에서 바뀌면 refreshChatInfo 가 칸을 다시 가른다.
    const RANK = { intake: 0, exit: 1 };
    const ROLE_LABEL = { intake: "사전 테스트", exit: "사후 테스트" };   // 챗 빌더 [대화 설정] 용도 이름과 같게
    const rankOf = (p) => RANK[p.role] ?? 2;
    const isTest = (p) => rankOf(p) < 2;
    // Array.sort 는 안정 정렬이라 같은 칸 안의 저작 순서는 그대로 남는다.
    const ordered = (list) => [...list].sort((a, b) => rankOf(a) - rankOf(b));

    const load = () => {
        loading = true;
        PRConnect.send("/api/HubSetup/getHubChats", { botId }, (data) => {
            gridColumns = Number(data.result?.gridColumns) || 0;
            cardStyle = data.result?.cardStyle || "full";
            defaultDisplay = splitDisplay(data.result?.progressDisplay, "progress");
            picked = ordered((data.result?.chats || []).map((r) => ({
                chatId: r.chatId, title: r.title, subTitle: r.subTitle,
                label: r.label || "", published: Number(r.published) > 0,
                // 용도는 카드가 아니라 챗이 갖는다(CHATS.PURPOSE). ⚠️ getHubChats 는 매퍼 행을 그대로 줘서
                // 필드 이름이 `purpose` 다 — `role` 로 바꿔 주는 건 학습자 화면용 Bot.hubChatCard 뿐이다.
                // 예전엔 r.role 을 읽어 늘 'normal' 이 됐다(담아 둔 사전 테스트가 본 콘텐츠에 섞이고 사전 자리는 비어 보임).
                role: r.purpose || "normal",
                thumb: thumbOf(r), ...splitDisplay(r.progressDisplay),
                // 카드 아이콘(BOT_CHATS.THUMB_ASSET_ID) — 저장은 ref, 화면은 서버가 current 로 푼 URL 을 쓴다
                iconAssetId: r.thumbAssetId || null, iconUrl: r.thumbAssetUrl || null,
            })));
            PRConnect.send("/api/HubSetup/getChatCandidates", { botId }, (d2) => {
                candidates = (d2.result || []).map(toCandidate);
                loading = false;
            });
        });
    };

    // 채팅 빌더는 **새 탭**으로 연다 — 허브 저작 화면을 그대로 두고 오가게.
    // 자동저장 대기(scheduleSave)는 이 탭에서 계속 돌므로 열기 전에 서둘러 저장할 필요가 없다.
    const openBuilder = (chatId) => window.open(`/chat-builder/${chatId}`, "_blank");

    // 그 탭에서 이름·썸네일·용도를 바꾸거나 게시하고 돌아오면 카드에 반영한다('미게시'가 남아 있으면 헷갈린다).
    // ⚠️ load() 를 다시 부르지 않는다 — 순서·카드 이름·표시는 **이 화면 소유**라 서버 값으로 덮으면
    //    저장 대기 중인 편집이 날아간다. 챗이 가진 값(제목·설명·썸네일·게시·용도)만 골라 덮는다.
    const refreshChatInfo = () => {
        if (document.visibilityState !== "visible" || loading) return;
        PRConnect.send("/api/HubSetup/getChatCandidates", { botId }, (d2) => {
            candidates = (d2.result || []).map(toCandidate);
            const byId = new Map(candidates.map((c) => [c.id, c]));
            picked = ordered(picked.map((p) => {
                const c = byId.get(p.chatId);
                return c
                    ? { ...p, title: c.title, subTitle: c.subTitle, thumb: c.thumb, published: c.published, role: c.purpose || "normal" }
                    : p;
            }));
        });
    };

    onMount(() => {
        load();
        document.addEventListener("visibilitychange", refreshChatInfo);
    });
    onDestroy(() => document.removeEventListener("visibilitychange", refreshChatInfo));

    $: pickedIds = new Set(picked.map((p) => p.chatId));
    $: defaultLabel = displayLabel(defaultDisplay);
    // 기본값과 다르게 정한 본 콘텐츠 카드 수 — 기본값 상자에 같이 보여 준다
    $: overrideCount = picked.filter((p) => !isTest(p) && p.kind).length;
    $: testCount = picked.filter(isTest).length;
    // 사전·사후 반쪽 자리에 들어갈 것 — 담긴 카드와, 그 용도인데 아직 안 담긴 후보
    $: intakeItems = picked.filter((p) => p.role === "intake");
    $: exitItems = picked.filter((p) => p.role === "exit");
    $: intakeEligible = candidates.filter((c) => c.purpose === "intake" && !pickedIds.has(c.id));
    $: exitEligible = candidates.filter((c) => c.purpose === "exit" && !pickedIds.has(c.id));

    const add = (c) => {
        if (pickedIds.has(c.id)) return;
        picked = ordered([...picked, {
            chatId: c.id, title: c.title, subTitle: c.subTitle, label: "",
            published: c.published, thumb: c.thumb, kind: "", key: "", role: c.purpose || "normal",   // 새 카드는 기본값 따름
            iconAssetId: null, iconUrl: null,
        }]);
        // 본 콘텐츠 칸은 높이 상한 안에서 스크롤된다 — 새 카드는 맨 뒤에 붙어 안 보이므로 끝까지 내려 보여 준다.
        // (사전·사후는 자기 자리가 따로 있어 스크롤 칸에 안 들어간다)
        if (rankOf({ role: c.purpose }) === 2) {
            tick().then(() => mainScroll?.scrollTo({ top: mainScroll.scrollHeight, behavior: "smooth" }));
        }
        scheduleSave();
    };
    const remove = (i) => {
        picked = picked.filter((_, idx) => idx !== i);
        scheduleSave();
    };
    const removeChat = (chatId) => {
        picked = picked.filter((p) => p.chatId !== chatId);
        scheduleSave();
    };

    // ── 카드 아이콘 ── 고르는 창은 하나만 두고, 어느 카드 것인지(iconFor)만 바꿔 연다.
    let iconFor = null;
    let iconPickerOpen = false;
    $: iconCurrent = picked.find((p) => p.chatId === iconFor)?.iconAssetId ?? null;
    const pickIcon = (chatId) => {
        iconFor = chatId;
        iconPickerOpen = true;
    };
    const setIcon = (chatId, assetId, url) => {
        picked = picked.map((p) => (p.chatId === chatId ? { ...p, iconAssetId: assetId, iconUrl: url } : p));
        scheduleSave();
    };
    const onIconPicked = (e) => {
        iconPickerOpen = false;
        if (iconFor) setIcon(iconFor, e.detail?.assetId || null, e.detail?.url || null);
    };

    // 사후 빈 자리의 [사전 테스트로 만들기] — 서버가 챗을 만들고(설정 복사 + 문항은 사전 테스트를 가리킴),
    // **격자에 담는 건 여기서** 한다(add → 자동저장). 서버가 BOT_CHATS 에 직접 넣으면 이 화면이 들고 있는
    // 저장 전 격자와 두 군데서 쓰게 되어, 다음 자동저장(전체 교체)이 그 행을 지운다.
    let creatingExit = false;
    const createExit = (fromChatId) => {
        if (creatingExit) return;
        creatingExit = true;
        error = "";
        PRConnect.send("/api/HubSetup/createExitFromIntake", { botId, chatId: fromChatId }, (data) => {
            creatingExit = false;
            const c = toCandidate(data.result || {});
            if (!c.id) return;
            candidates = [...candidates, c];
            add(c);
        }, (err) => {
            creatingExit = false;
            error = err.header?.message || "사후 테스트를 만들지 못했습니다.";
        });
    };
    // 같은 칸(같은 용도) 안에서만 움직인다 — 칸을 넘으면 '사전·사후는 맨 위' 규칙이 깨진다.
    const canMove = (list, i, d) => {
        const j = i + d;
        return j >= 0 && j < list.length && rankOf(list[j]) === rankOf(list[i]);
    };
    const move = (i, d) => {
        const j = i + d;
        if (!canMove(picked, i, d)) return;
        const next = [...picked];
        [next[i], next[j]] = [next[j], next[i]];
        picked = next;
        scheduleSave();
    };

    const scheduleSave = () => {
        clearTimeout(saveTimer);
        saveTimer = setTimeout(save, SAVE_DEBOUNCE_MS);
    };

    const save = () => {
        error = "";
        markSaving();
        PRConnect.send("/api/HubSetup/setHubChats", {
            botId, gridColumns, cardStyle,
            progressDisplay: joinDisplay(defaultDisplay) || "progress",
            chats: picked.map((p) => ({
                // 사전·사후 테스트는 진행 표시를 안 쓴다(빌더에서 칸을 감췄다) — 남아 있던 값이 학습자 카드에
                // 빈 막대로 뜨지 않게 'none' 으로 저장한다.
                chatId: p.chatId, label: p.label || null, progressDisplay: isTest(p) ? "none" : joinDisplay(p),
                // ⚠️ 빠뜨리면 저장(전체 교체)할 때마다 카드 아이콘이 지워진다
                thumbAssetId: p.iconAssetId || null,
            })),
        }, () => {
            markSaved();
            // ⚠️ 여기서 목록을 다시 불러오지 않는다. 저장이 바꾸는 값(published 등)이 없는데
            //    재조회하면 화면이 통째로 다시 그려져 '눌렀더니 리프레시된다'로 느껴진다.
            dispatch("saved");   // 미리보기만 갱신
        }, (err) => {
            markSaved();         // pill 이 'saving' 에 갇히지 않게 되돌린다
            error = err.header?.message || "저장하지 못했습니다.";
        });
    };

    onDestroy(() => clearTimeout(saveTimer));
</script>

{#if loading}
    <p class="tw-mb-0 tw-py-6 tw-text-sm tw-text-muted-foreground">불러오는 중…</p>
{:else}
<!-- 두 섹션 사이 간격 — 다른 pageType 의 콘텐츠 탭(ContentsBlock)과 같은 tw-space-y-4.
     감싸지 않으면 두 카드가 맞붙는다. -->
<div class="tw-space-y-4">
    <Section title="격자에 담긴 채팅" description="허브 페이지에 카드로 뜨는 순서 그대로입니다.">
        <div class="tw-space-y-3">
            <!-- 저장 버튼이 없다. 빌더 관례대로 자동저장이고, 진행 상태는 상단 톱바의
                 '저장 중…/저장됨' 표시가 알려준다(store/builder-store.js). -->
            <!-- 격자 '전체 모양' 둘을 한 상자로 묶는다 — 아래 카드별 설정과 축이 달라서
                 (여기는 격자 전체, 저기는 카드 한 장), 섞여 있으면 어느 쪽이 뭘 바꾸는지 안 읽힌다. -->
            <div class="tw-rounded-lg tw-border tw-border-solid tw-border-border tw-bg-muted/30 tw-p-3">
                <div class="tw-grid tw-gap-3 sm:tw-grid-cols-2">
                    <label class="tw-mb-0 tw-space-y-1 tw-font-normal">
                        <span class="tw-block tw-text-xs tw-font-medium tw-text-foreground">한 줄에 보여줄 카드 수</span>
                        <Select bind:value={gridColumns} on:change={scheduleSave}>
                            {#each COLUMNS as c (c.value)}<option value={c.value}>{c.label}</option>{/each}
                        </Select>
                        <span class="tw-block tw-text-xs tw-text-muted-foreground">
                            '자동'은 화면이 넓어질수록 열이 늘어납니다.
                        </span>
                    </label>
                    <label class="tw-mb-0 tw-space-y-1 tw-font-normal">
                        <span class="tw-block tw-text-xs tw-font-medium tw-text-foreground">카드에 보여줄 내용</span>
                        <Select bind:value={cardStyle} on:change={scheduleSave}>
                            {#each CARD_STYLES as s (s.value)}<option value={s.value}>{s.label}</option>{/each}
                        </Select>
                        <span class="tw-block tw-text-xs tw-text-muted-foreground">
                            '썸네일만'은 이미지가 카드 폭을 채웁니다.
                        </span>
                    </label>
                    <label class="tw-mb-0 tw-space-y-1 tw-font-normal sm:tw-col-span-2">
                        <span class="tw-block tw-text-xs tw-font-medium tw-text-foreground">카드 진행 표시 (기본값)</span>
                        <div class="tw-flex tw-gap-2">
                            <Select class="tw-min-w-0 tw-flex-1" bind:value={defaultDisplay.kind} on:change={scheduleSave}>
                                {#each DISPLAY as d (d.value)}<option value={d.value}>{d.label}</option>{/each}
                            </Select>
                            {#if defaultDisplay.kind === "metric"}
                                <Input class="tw-w-32" type="text" placeholder="지표 키 (affinity)"
                                       value={defaultDisplay.key}
                                       on:change={(e) => { defaultDisplay.key = e.target.value; scheduleSave(); }} />
                            {/if}
                        </div>
                        <span class="tw-block tw-text-xs tw-text-muted-foreground">
                            모든 카드가 이 표시를 따릅니다. 다르게 할 카드만 그 카드의 '진행 표시'에서 바꾸세요{overrideCount ? ` (지금 ${overrideCount}장이 따로 정함)` : ""}.
                        </span>
                    </label>
                </div>
            </div>

            {#if error}<p class="tw-mb-0 tw-text-sm tw-text-destructive">{error}</p>{/if}

            <!-- ⚠️ ul/li 를 쓰지 않는다. tailwind preflight 가 꺼져 있어(tailwind.config.js)
                 webflow.css 의 전역 `ul,ol { padding-left:40px; margin-bottom:10px }` 과 기본 불릿이
                 그대로 먹는다 — 목록이 들여쓰기된 채 점이 붙어 '디자인이 안 먹은' 꼴이 된다.
                 이 레포에서 ul 은 실제 불릿 목록일 때만 쓴다(tw-list-disc 와 함께). -->
            <!-- 카드를 **트레이 위에** 올린다(한 단계 더 감싼다). 섹션도 카드도 흰 바탕이라
                 바로 얹으면 테두리 한 줄로만 갈리는데, 그 테두리마저 안 그려졌다 —
                 ⚠️ preflight 가 꺼져 있어 `tw-border` 는 border-width 만 준다(border-style 초기값 none).
                    div 에 테두리를 그리려면 `tw-border-solid` 를 **같이** 붙여야 한다(ChatBuilder 와 같은 처리).
                 트레이는 둘이다: 사전·사후(primary 옅은 색, 맨 위) / 본 콘텐츠(회색, 순번). picked 는
                 늘 그 순서로 정렬돼 있어서(ordered) 사전·사후는 앞 testCount 장, 본 콘텐츠는 그 뒤다. -->

            <!-- 사전·사후: 한 카드를 **반으로** 갈라 자리 둘을 늘 보여 준다(왼쪽=사전, 오른쪽=사후).
                 목록 사이에 섞어 두면 챗이 많을 때 찾기 어렵고, '비어 있다'는 것도 안 보였다 —
                 빈 자리는 ＋ 로, 찬 자리는 그 챗 카드로 그린다(HubTestSlot). 학습자 화면 순서도 사전→메인→사후. -->
            <div class="tw-rounded-xl tw-border tw-border-solid tw-border-primary/25 tw-bg-primary/5 tw-p-2.5">
                <div class="tw-px-1 tw-pb-2.5 tw-pt-0.5">
                    <p class="tw-mb-0 tw-text-xs tw-font-semibold tw-text-primary">사전·사후 테스트</p>
                    <p class="tw-mb-0 tw-mt-0.5 tw-text-xs tw-text-muted-foreground">
                        사전 테스트를 마쳐야 본 콘텐츠가 열리고, 사후 테스트는 본 콘텐츠를 다 마치거나 운영자가 열면 나타나요.
                    </p>
                </div>
                <div class="tw-grid tw-gap-2.5 sm:tw-grid-cols-2">
                    <HubTestSlot role="intake" items={intakeItems} eligible={intakeEligible}
                                 on:pickIcon={(e) => pickIcon(e.detail)} on:clearIcon={(e) => setIcon(e.detail, null, null)}
                                 on:add={(e) => add(e.detail)} on:remove={(e) => removeChat(e.detail)}
                                 on:open={(e) => openBuilder(e.detail)} on:change={scheduleSave} />
                    <HubTestSlot role="exit" items={exitItems} eligible={exitEligible}
                                 createFrom={intakeItems[0] ?? null} busy={creatingExit}
                                 on:create={(e) => createExit(e.detail)}
                                 on:pickIcon={(e) => pickIcon(e.detail)} on:clearIcon={(e) => setIcon(e.detail, null, null)}
                                 on:add={(e) => add(e.detail)} on:remove={(e) => removeChat(e.detail)}
                                 on:open={(e) => openBuilder(e.detail)} on:change={scheduleSave} />
                </div>
            </div>

            <div class="tw-rounded-xl tw-bg-muted/60 tw-p-2">
                <p class="tw-mb-0 tw-px-1.5 tw-pb-2 tw-pt-1 tw-text-xs tw-font-semibold tw-text-muted-foreground">
                    본 콘텐츠 · {picked.length - testCount}
                </p>
                {#if picked.length === testCount}
                    <p class="tw-mx-0.5 tw-mb-0.5 tw-rounded-md tw-border tw-border-dashed tw-border-border tw-bg-card/60 tw-px-3 tw-py-6 tw-text-center tw-text-sm tw-text-muted-foreground">
                        아직 없습니다. 아래 목록에서 담아주세요.
                    </p>
                {:else}
                    <!-- 카드가 쌓이면 이 칸이 '내 채팅'을 화면 밖으로 밀어낸다 → 높이 상한 + 안에서 스크롤.
                         머리(본 콘텐츠 · N)는 스크롤 밖에 둬서 몇 장인지 늘 보이게 한다.
                         ⚠️ 스크롤 칸의 p-0.5 는 카드 그림자·테두리가 overflow 에 잘리지 않게 하는 여유(트레이 p-2 + 0.5 = 위 칸들의 2.5). -->
                    <div bind:this={mainScroll} class="tw-max-h-[560px] tw-space-y-2.5 tw-overflow-y-auto tw-p-0.5">
                        {#each picked.slice(testCount) as p, k (p.chatId)}
                            <HubPickedCard {p} order={k + 1} displayOptions={DISPLAY} {defaultLabel}
                                           canUp={canMove(picked, testCount + k, -1)} canDown={canMove(picked, testCount + k, 1)}
                                           on:open={() => openBuilder(p.chatId)}
                                           on:up={() => move(testCount + k, -1)} on:down={() => move(testCount + k, 1)}
                                           on:remove={() => remove(testCount + k)} on:change={scheduleSave}
                                           on:pickIcon={() => pickIcon(p.chatId)} on:clearIcon={() => setIcon(p.chatId, null, null)} />
                        {/each}
                    </div>
                {/if}
            </div>
        </div>
    </Section>

    <Section title="내 채팅" description="담으면 허브 격자에 카드로 나타납니다. V3 채팅만 담을 수 있어요.">
        {#if candidates.length === 0}
            <p class="tw-mb-0 tw-text-sm tw-text-muted-foreground">
                담을 수 있는 V3 채팅이 없습니다. 채팅을 만들고 V3로 전환해주세요.
            </p>
        {:else}
            <!-- 계정에 챗이 쌓이면 목록이 화면을 넘긴다 → 검색 + 높이 상한.
                 담긴 것/안 담긴 것을 섞어 두되(순서가 바뀌면 어디 갔는지 못 찾는다) 카드 색과 버튼으로 구분한다. -->
            <div class="tw-mb-3 tw-flex tw-items-center tw-gap-2">
                <div class="tw-relative tw-min-w-0 tw-flex-1">
                    <svg class="tw-pointer-events-none tw-absolute tw-left-3 tw-top-1/2 tw-h-4 tw-w-4 -tw-translate-y-1/2 tw-text-muted-foreground"
                         viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                         stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                        <circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" />
                    </svg>
                    <!-- 왼쪽 여백은 inline — cn 은 단순 결합이라 tw-pl-* 를 주면 Input 기본 tw-px-3 과 둘 다 남아
                         어느 쪽이 이길지가 빌드물 순서에 달린다(lib/ui/Input.svelte 주석). -->
                    <Input type="text" placeholder="채팅 이름으로 찾기" style="padding-left: 2.25rem"
                           value={keyword}
                           on:input={(e) => (keyword = e.target.value)} />
                </div>
                <span class="tw-shrink-0 tw-rounded-full tw-bg-muted tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-tabular-nums tw-text-muted-foreground">
                    {shown.length}/{candidates.length}
                </span>
            </div>
            {#if shown.length === 0}
                <p class="tw-mb-0 tw-rounded-xl tw-border-2 tw-border-dashed tw-border-border tw-bg-muted/40 tw-px-3 tw-py-8 tw-text-center tw-text-sm tw-text-muted-foreground">
                    '{keyword}' 와 맞는 채팅이 없습니다.
                </p>
            {:else}
            <!-- 위 '본 콘텐츠'와 같은 언어로: 회색 트레이 위에 흰 카드를 띄운다.
                 ⚠️ 예전엔 tw-divide-y + tw-border 로 줄을 그었는데 preflight off 라 border-style 이 none —
                    구분선도 바깥 테두리도 안 그려져 행이 맞붙어 보였다. 카드 테두리엔 tw-border-solid 를 같이 준다.
                 열 수는 auto-fill 로 **패널 폭**에 맞춘다(카드 최소 280px) — 빌더 에디터 폭은 사이드바·미리보기에
                 따라 바뀌어서 sm:/lg: 같은 뷰포트 기준으로는 좁은 패널에 두 줄을 욱여넣게 된다. -->
            <div class="tw-max-h-[480px] tw-overflow-y-auto tw-rounded-xl tw-bg-muted/60 tw-p-2.5">
                <div class="tw-grid tw-grid-cols-[repeat(auto-fill,minmax(280px,1fr))] tw-gap-2.5">
                    {#each shown as c (c.id)}
                        {@const isPicked = pickedIds.has(c.id)}
                        <div class="tw-flex tw-items-center tw-gap-3 tw-rounded-lg tw-border tw-border-solid tw-px-3 tw-py-3 tw-shadow-sm tw-transition-colors {isPicked
                                ? 'tw-border-primary/30 tw-bg-primary/5'
                                : 'tw-border-border tw-bg-card hover:tw-border-primary/30'}">
                            {#if c.thumb}
                                <img class="tw-h-11 tw-w-11 tw-shrink-0 tw-rounded-md tw-object-cover" src={c.thumb} alt="" loading="lazy" />
                            {:else}
                                <div class="tw-flex tw-h-11 tw-w-11 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-bg-muted tw-text-sm tw-font-semibold tw-text-muted-foreground">
                                    {(c.title || "?").slice(0, 1)}
                                </div>
                            {/if}
                            <div class="tw-min-w-0 tw-flex-1">
                                <p class="tw-mb-0 tw-truncate tw-text-sm tw-font-semibold tw-text-foreground" title={c.title}>{c.title}</p>
                                {#if c.subTitle}
                                    <p class="tw-mb-0 tw-mt-0.5 tw-truncate tw-text-xs tw-text-muted-foreground" title={c.subTitle}>{c.subTitle}</p>
                                {/if}
                                <!-- 배지는 제목 아래 줄로 — 제목 옆에 두면 반쪽 폭 카드에서 제목이 먼저 잘린다.
                                     버전 배지는 뺐다 — 후보가 전부 V3 라 아무것도 구분해 주지 못한다.
                                     대신 실제로 판단에 쓰이는 '미게시'를 보여준다(담아도 실사용자가 못 연다).
                                     ⚠️ Badge outline 은 tw-border-solid 가 없어 테두리가 안 그려진다 → 위 카드들과 같은 required. -->
                                {#if ROLE_LABEL[c.purpose] || !c.published}
                                    <div class="tw-mt-1.5 tw-flex tw-flex-wrap tw-items-center tw-gap-1">
                                        {#if ROLE_LABEL[c.purpose]}
                                            <span class="tw-inline-flex tw-items-center tw-rounded-full tw-bg-primary/10 tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-text-primary">
                                                {ROLE_LABEL[c.purpose]}
                                            </span>
                                        {/if}
                                        {#if !c.published}<Badge variant="required">미게시</Badge>{/if}
                                    </div>
                                {/if}
                            </div>
                            {#if isPicked}
                                <span class="tw-inline-flex tw-h-9 tw-shrink-0 tw-items-center tw-gap-1 tw-px-2 tw-text-xs tw-font-semibold tw-text-primary">
                                    <svg class="tw-h-4 tw-w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                                         stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>
                                    담김
                                </span>
                            {:else}
                                <Button variant="outline" size="sm" class="tw-shrink-0" on:click={() => add(c)}>
                                    <svg class="tw-h-3.5 tw-w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                                         stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14" /><path d="M12 5v14" /></svg>
                                    담기
                                </Button>
                            {/if}
                        </div>
                    {/each}
                </div>
            </div>
            {/if}
        {/if}
    </Section>
</div>
{/if}

<ImagePicker
    bind:open={iconPickerOpen}
    mode="single"
    contextFolder={bot ? botAssetFolder(bot) : null}
    currentAssetId={iconCurrent}
    on:select={onIconPicked}
/>
